import { useEffect, useMemo } from "react";

import useDeepCompare from "@alpaca-travel/utils/use-deep-compare";
import { MapEventListener, MapEventType } from "./types";
import useMap from "./use-map";

type MapEventOptions = {
  once?: boolean;
};

type UseMapEvent = {
  (type: MapEventType, listener: MapEventListener): void;
  (
    type: MapEventType,
    listener: MapEventListener,
    options: MapEventOptions
  ): void;
};

const useMapEvent: UseMapEvent = (
  type: MapEventType,
  listener: MapEventListener,
  opts?: Options
) => {
  // Incase passing in an object
  const latestOpts = useDeepCompare(opts);

  // Whether we should only update once
  const once = useMemo(() => {
    if (latestOpts != null) {
      return latestOpts.once || false;
    }
    return false;
  }, [latestOpts]);

  // Obtain the map
  const { map } = useMap();

  useEffect(() => {
    const mounted = true;
    const safe = (fn: Function, ...args: any[]) => {
      if (mounted && fn) {
        fn(...args);
      }
    };

    const safeListener = (...args: any[]) => safe(listener, ...args);

    // Determine if we are ongoing or not
    if (map && map.on && map.once) {
      if (once) {
        map.once(type, safeListener);
      } else {
        map.on(type, safeListener);
      }
    }

    return () => {
      if (map && map.off) {
        map.off(type, safeListener);
      }
    };
  }, [map, type, listener, once]);
};

export default useMapEvent;

type Options = {
  mapRef?: string;
  once?: boolean;
};
